<template>
  <div class="modal fade" id="recordingAddToListModal" ref="modalElement" tabindex="-1" aria-labelledby="recordingAddToListModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content p-3">
        <div class="modal-header">
          <h5 class="modal-title" id="recordingAddToListModalLabel"><strong>{{ $t('recordings.show.add_to_list_title') }}</strong></h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('shared.close')"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-light" role="status">
              <span class="visually-hidden">{{ $t('shared.loading') }}</span>
            </div>
          </div>

          <p v-else-if="loadError" class="text-danger">{{ $t('recordings.show.could_not_load_lists') }}</p>

          <template v-else>
            <p v-if="lists.length === 0" class="text-muted">{{ $t('recordings.show.no_lists_yet') }}</p>

            <div v-for="list in lists" :key="list.id">
              <div class="d-flex align-items-center justify-content-between">
                <p class="m-0 py-2">{{ list.name }}</p>
                <button
                  class="btn btn-link d-flex align-items-center"
                  type="button"
                  :disabled="pendingListIds.includes(String(list.id))"
                  @click="toggle(list)"
                >
                  <span v-if="isInList(list)" class="material-icons text-yellow">check</span>
                  <span v-else class="material-icons text-muted">add</span>
                </button>
              </div>
              <hr class="my-1">
            </div>

            <div class="d-flex align-items-center">
              <input
                v-model="newListName"
                class="form-control py-2 border-0"
                style="background: #17181d"
                :placeholder="$t('on_boarding.add_to_list_page.create_a_new_list')"
                @keyup.enter="createList"
              >
              <button class="btn btn-link d-flex align-items-center" type="button" :disabled="creating" @click="createList">
                <span class="material-icons text-muted">add</span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { Modal } from "bootstrap";
import graph_client from "../../utils/graphql/graph_client";
import { CreateHikeList } from "../../utils/graphql/user-graph";
import {
  AddRecordingToList,
  GetUserListsForRecording,
  RemoveRecordingFromList,
} from "../../utils/graphql/hike-list-graph";
import {
  isRecordingInList,
  listsAcceptingRecordings,
  RecordingListLike,
  toggleRecordingMembership,
} from "../../utils/helpers/recordingListMembership";
import { i18n } from "../../i18n";

const PER_PAGE = 100;

export default defineComponent({
  props: {
    recordingId: { type: String, required: true },
  },
  setup(props) {
    const modalElement = ref<HTMLElement | null>(null);
    const lists = ref<RecordingListLike[]>([]);
    const loading = ref(false);
    const loadError = ref(false);
    const pendingListIds = ref<string[]>([]);
    const newListName = ref("");
    const creating = ref(false);

    const loadLists = async () => {
      loading.value = true;
      loadError.value = false;
      try {
        const all: RecordingListLike[] = [];
        for (let page = 1; ; page += 1) {
          const response = await graph_client.request(GetUserListsForRecording, { page, perPage: PER_PAGE });
          const batch = response.searchUserHikeLists || [];
          all.push(...batch);
          if (batch.length < PER_PAGE) break;
        }
        lists.value = listsAcceptingRecordings(all);
      } catch (error) {
        console.error("Error loading lists:", error);
        loadError.value = true;
      } finally {
        loading.value = false;
      }
    };

    const isInList = (list: RecordingListLike) => isRecordingInList(list, props.recordingId);

    const toggle = async (list: RecordingListLike) => {
      const listId = String(list.id);
      if (pendingListIds.value.includes(listId)) return;

      const removing = isInList(list);
      const before = lists.value;
      // Flip optimistically; a failure puts the previous array back.
      lists.value = toggleRecordingMembership(before, listId, props.recordingId);
      pendingListIds.value = [...pendingListIds.value, listId];

      try {
        const variables = { hikeListId: listId, recordingId: props.recordingId };
        const response = removing
          ? (await graph_client.request(RemoveRecordingFromList, variables)).removeRecordingFromList
          : (await graph_client.request(AddRecordingToList, variables)).addRecordingToList;
        if (response.errors.length > 0) throw new Error(response.errors[0].errors[0]);
      } catch (error) {
        console.error("Error updating list:", error);
        lists.value = before;
        alert(i18n.global.t('recordings.show.could_not_update_list'));
      } finally {
        pendingListIds.value = pendingListIds.value.filter((id) => id !== listId);
      }
    };

    const createList = async () => {
      const name = newListName.value.trim();
      if (!name) {
        alert(i18n.global.t('on_boarding.add_to_list_page.add_a_name_for_list'));
        return;
      }
      creating.value = true;
      try {
        const created = await graph_client.request(CreateHikeList, { input: { name } });
        if (created.createHikeList.errors.length > 0) throw new Error(created.createHikeList.errors[0].errors[0]);

        const list = created.createHikeList.hikeList;
        const added = await graph_client.request(AddRecordingToList, { hikeListId: list.id, recordingId: props.recordingId });
        if (added.addRecordingToList.errors.length > 0) throw new Error(added.addRecordingToList.errors[0].errors[0]);

        lists.value = [...lists.value, { id: list.id, name: list.name, kind: null, itemsCount: 1, recordingIds: [props.recordingId] }];
        newListName.value = "";
      } catch (error: any) {
        console.error("Error creating list:", error);
        alert(error?.message || i18n.global.t('recordings.show.could_not_update_list'));
      } finally {
        creating.value = false;
      }
    };

    onMounted(() => {
      document.getElementById('show-add-to-list-modal')?.addEventListener('click', () => {
        if (!modalElement.value) return;
        Modal.getOrCreateInstance(modalElement.value).show();
        loadLists();
      });
    });

    return { modalElement, lists, loading, loadError, pendingListIds, newListName, creating, isInList, toggle, createList };
  },
});
</script>
